import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, type MouseEvent } from "react";
import { useConversationInfoCard } from "../hooks/use-conversation-info-card";
import {
  MessageChannelIcons,
  MessageChannelReadable,
} from "../ui/message-channel-ui";
import { PriorityBadge, StatusBadge } from "./badges/conversation-badges";

export interface ConversationInfoCardProps {
  conversation: ConversationListItemResponse;
  isActive: boolean;
  onClick: (event: MouseEvent<HTMLDivElement>) => void;
}

export const ConversationInfoCard = memo(function ConversationInfoCard({
  conversation,
  isActive,
  onClick,
}: ConversationInfoCardProps) {
  const { channels, isUnread, timeSinceMessage, lastMessageDate } =
    useConversationInfoCard({ conversation });

  return (
    <Flex
      bgColor={isActive ? "secondary-solid" : "primary"}
      borderBottomWidth="1px"
      borderColor="secondary"
      borderStyle="solid"
      cursor="pointer"
      onClick={onClick}
      p="lg"
      style={{ transition: "background-color 0.15s ease" }}
    >
      <Flex dir="column" gap="sm">
        {/* Row 1: Header - Name/time column + unread dot */}
        <Flex align="flex-start" justify="space-between">
          <Flex dir="column" gap="none">
            <Text fontSize="xs" fontWeight="semibold" textColor="primary">
              {/* TODO: Replace with actual customer name when available */}
              CUSTOMER NAME
            </Text>
            {lastMessageDate && (
              <Tooltip arrow title={lastMessageDate.toLocaleString()}>
                <span>
                  <Text fontSize="xxs" textColor="tertiary">
                    {timeSinceMessage}
                  </Text>
                </span>
              </Tooltip>
            )}
          </Flex>
          {isUnread && (
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "var(--redo-colors-foreground-fg-error)",
                flexShrink: 0,
                marginTop: 4,
              }}
            />
          )}
        </Flex>

        {/* Row 2: Subject + Preview */}
        <Flex dir="column" gap="none">
          {conversation.summary && (
            <Text
              fontSize="xs"
              style={{
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
              textColor="tertiary"
              textOverflow="ellipsis"
            >
              {conversation.summary}
            </Text>
          )}
        </Flex>

        {/* Row 3: Badges */}
        <Flex align="center" gap="xs">
          {channels && channels.length > 0 ? (
            channels.map((channel) => (
              <ChannelBadge channel={channel} key={channel} />
            ))
          ) : (
            <ChannelBadge />
          )}
          <StatusBadge showIcon size="xs" status={conversation.status} />
          {conversation.priority && (
            <PriorityBadge priority={conversation.priority} size="xs" />
          )}
        </Flex>

        {/* Row 4: Avatar group placeholder */}
        <AvatarGroupPlaceholder />
      </Flex>
    </Flex>
  );
});

interface ChannelBadgeProps {
  /** Optional since threads may not be loaded yet */
  channel?: MessageChannel;
}

const ChannelBadge = memo(function ChannelBadge({
  channel,
}: ChannelBadgeProps) {
  if (!channel) {
    return <RedoBadge color="gray" size="xs" text="CHANNEL" />;
  }

  const displayName = MessageChannelReadable[channel];
  const Icon = MessageChannelIcons[channel];

  return (
    <RedoBadge
      color="gray"
      segmentLeading={{ type: "icon", Icon }}
      size="xs"
      text={displayName}
    />
  );
});

const AvatarGroupPlaceholder = memo(function AvatarGroupPlaceholder() {
  const avatarSize = 24;
  const overlap = 8;

  return (
    <Flex align="center">
      {/* Stacked avatar placeholders */}
      <Flex position="relative">
        {[0, 1, 2].map((index) => (
          <div
            key={index}
            style={{
              width: avatarSize,
              height: avatarSize,
              borderRadius: "50%",
              backgroundColor: "var(--redo-colors-background-bg-tertiary)",
              border: "2px solid var(--redo-colors-background-bg-primary)",
              marginLeft: index === 0 ? 0 : -overlap,
              position: "relative",
              zIndex: 3 - index,
            }}
          />
        ))}
      </Flex>
      {/* Overflow count */}
      <Text fontSize="xxs" ml="xs" textColor="tertiary">
        +2
      </Text>
    </Flex>
  );
});

export interface ConversationInfoCardSkeletonProps {
  count?: number;
}

export const ConversationInfoCardSkeleton = memo(
  function ConversationInfoCardSkeleton({
    count = 3,
  }: ConversationInfoCardSkeletonProps) {
    return (
      <>
        {Array.from({ length: count }).map((_, index) => (
          <Flex
            borderBottomWidth="1px"
            borderColor="secondary"
            borderStyle="solid"
            key={index}
            p="lg"
          >
            <Flex dir="column" gap="sm">
              <Flex align="center" gap="sm" justify="space-between">
                <div
                  style={{
                    width: 100,
                    height: 14,
                    backgroundColor:
                      "var(--redo-colors-background-bg-tertiary)",
                    borderRadius: 4,
                  }}
                />
                <div
                  style={{
                    width: 40,
                    height: 12,
                    backgroundColor:
                      "var(--redo-colors-background-bg-tertiary)",
                    borderRadius: 4,
                  }}
                />
              </Flex>
              <div
                style={{
                  width: "100%",
                  height: 28,
                  backgroundColor: "var(--redo-colors-background-bg-tertiary)",
                  borderRadius: 4,
                }}
              />
              <Flex align="center" gap="xs">
                <div
                  style={{
                    width: 50,
                    height: 20,
                    backgroundColor:
                      "var(--redo-colors-background-bg-tertiary)",
                    borderRadius: 10,
                  }}
                />
              </Flex>
            </Flex>
          </Flex>
        ))}
      </>
    );
  },
);

export const ConversationInfoCardEmpty = memo(
  function ConversationInfoCardEmpty() {
    return (
      <Flex align="center" justify="center" p="xl">
        <Text fontSize="xs" textColor="tertiary">
          No conversations
        </Text>
      </Flex>
    );
  },
);
